import type { ReactNode, CSSProperties } from 'react'
import { useSearch } from '../contexts/SearchContext'
import { useCrosswordSettings } from '../contexts/CrosswordSettingsContext'
import type { Size, CrosswordColors } from '../contexts/CrosswordSettingsContext'

export type { Size }

const sizeConfig: Record<Size, { cell: string; font: string }> = {
  '2xs': { cell: 'w-2.5 h-2.5', font: 'text-[7px]' },
  'xs': { cell: 'w-3.5 h-3.5', font: 'text-[8px]' },
  'sm': { cell: 'w-4.5 h-4.5', font: 'text-[10px]' },
  'base': { cell: 'w-5.5 h-5.5', font: 'text-xs' },
  'lg': { cell: 'w-7 h-7', font: 'text-sm' },
  'xl': { cell: 'w-8 h-8', font: 'text-base' },
  '2xl': { cell: 'w-9 h-9', font: 'text-lg' },
  '3xl': { cell: 'w-11 h-11', font: 'text-xl' },
}

export function getCompactCrosswordSize(texts: string[]): Size {
  const maxLetters = Math.max(...texts.map(text => text.replace(/\s/g, '').length), 0)
  if (maxLetters > 18) return '2xs'
  if (maxLetters > 12) return 'xs'
  if (maxLetters > 7) return 'sm'
  return 'base'
}

type HighlightType = 'horizontal' | 'vertical' | null

type CrosswordLetterProps = {
  letter: string
  cellClass: string
  muted?: boolean
  inverted?: boolean
  highlightType?: HighlightType
  colors: CrosswordColors
  // Position flags for rounding outer edges
  isFirst?: boolean
  isLast?: boolean
  isTop?: boolean
  isBottom?: boolean
  className?: string
}

/**
 * Single letter cell - text styling inherited from parent
 */
function CrosswordLetter({
  letter,
  cellClass,
  muted = false,
  inverted = false,
  highlightType = null,
  colors,
  isFirst = false,
  isLast = false,
  isTop = true,
  isBottom = true,
  className = ''
}: CrosswordLetterProps) {
  // Build inline styles from colors context
  let style: CSSProperties = {}

  if (highlightType === 'horizontal') {
    style = {
      backgroundColor: colors.horizontalHighlightBg,
      borderColor: colors.horizontalHighlightBorder,
      color: colors.normalText,
    }
  } else if (highlightType === 'vertical') {
    style = {
      backgroundColor: colors.verticalHighlightBg,
      borderColor: colors.verticalHighlightBorder,
      color: colors.normalText,
    }
  } else if (inverted) {
    style = {
      backgroundColor: colors.invertedBg,
      borderColor: colors.invertedBorder,
      color: colors.invertedText,
    }
  } else if (muted) {
    style = {
      backgroundColor: '#f9fafb',
      borderColor: '#e5e7eb',
      color: '#9ca3af',
    }
  } else {
    style = {
      backgroundColor: colors.normalBg,
      borderColor: colors.normalBorder,
      color: colors.normalText,
    }
  }

  // Build rounding classes for outer edges
  const roundingClasses = [
    isFirst && isTop ? 'rounded-tl-sm' : '',
    isFirst && isBottom ? 'rounded-bl-sm' : '',
    isLast && isTop ? 'rounded-tr-sm' : '',
    isLast && isBottom ? 'rounded-br-sm' : '',
  ].filter(Boolean).join(' ')

  return (
    <span
      className={`
        ${cellClass}
        inline-flex items-center justify-center
        border font-medium font-sans
        ${roundingClasses}
        ${className}
      `}
      style={style}
    >
      {/* Hide placeholder character (□) - show empty cell instead */}
      {/* Use non-breaking space for empty to maintain cell height */}
      {letter === '□' ? '\u00A0' : letter.toUpperCase()}
    </span>
  )
}

type CrosswordWordProps = {
  text: string
  size?: Size
  muted?: boolean
  inverted?: boolean
  highlightIndex?: number | null
  highlightIndices?: number[]
  verticalHighlightIndices?: number[]
  autoHighlight?: boolean
  isPlaced?: boolean
  // Position flags for rounding (when used in pairs)
  isTopWord?: boolean
  isBottomWord?: boolean
  onLetterClick?: ((index: number) => void) | undefined
  onLetterHover?: ((index: number | null) => void) | undefined
  className?: string
}

/**
 * A word rendered as a row of letter cells (borders overlap via negative margin)
 * Text styling (size, weight, line-height, font) set on container and inherited
 */
export function CrosswordWord({
  text,
  size: sizeProp,
  muted = false,
  inverted = false,
  highlightIndex = null,
  highlightIndices = [],
  verticalHighlightIndices = [],
  autoHighlight = false,
  isPlaced = false,
  isTopWord = true,
  isBottomWord = true,
  onLetterClick,
  onLetterHover,
  className = ''
}: CrosswordWordProps) {
  const settings = useCrosswordSettings()
  const search = useSearch()

  // Use prop size if provided, otherwise use context size
  const size = sizeProp ?? settings.size
  const config = sizeConfig[size]
  const { colors } = settings
  const letters = text.replace(/\s/g, '')

  // Get horizontal highlight indices from search context if autoHighlight is enabled
  const searchHighlightIndices = autoHighlight && search
    ? search.getMatchIndices(letters)
    : []

  // Combine horizontal highlight sources
  const horizontalHighlights = new Set([
    ...highlightIndices,
    ...searchHighlightIndices,
    ...(highlightIndex !== null ? [highlightIndex] : [])
  ])

  // Vertical highlights (from pair context)
  const verticalHighlights = new Set(verticalHighlightIndices)

  // Determine highlight type for each index
  const getHighlightType = (idx: number): HighlightType => {
    // Vertical takes priority (more specific match)
    if (verticalHighlights.has(idx)) return 'vertical'
    if (horizontalHighlights.has(idx)) return 'horizontal'
    return null
  }

  return (
    <span
      className={`relative inline-flex leading-none font-medium ${config.font} ${className}`}
      style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      onMouseLeave={() => onLetterHover?.(null)}
    >
      {letters.split('').map((letter, idx) => (
        <span
          key={idx}
          onClick={() => onLetterClick?.(idx)}
          onMouseEnter={() => onLetterHover?.(idx)}
          className={`${onLetterClick ? 'cursor-pointer' : ''} ${idx > 0 ? '-ml-px' : ''}`}
        >
          <CrosswordLetter
            letter={letter}
            cellClass={config.cell}
            muted={muted}
            inverted={inverted}
            highlightType={getHighlightType(idx)}
            colors={colors}
            isFirst={idx === 0}
            isLast={idx === letters.length - 1}
            isTop={isTopWord}
            isBottom={isBottomWord}
          />
        </span>
      ))}
      {isPlaced && (
        <span className="absolute top-0 right-0 w-1 h-1 rounded-full bg-green-400 z-10" />
      )}
    </span>
  )
}

type CrosswordPairProps = {
  textA: string
  textB: string
  size?: Size
  highlightIndex?: number | null
  highlightIndices?: number[]
  autoHighlight?: boolean
  isPlaced?: boolean
  isPlacedA?: boolean
  isPlacedB?: boolean
  onLetterClick?: ((index: number) => void) | undefined
  onLetterHover?: ((index: number | null) => void) | undefined
  // Optional per-row click handlers; when provided they take precedence over
  // onLetterClick for that row.
  onRowAClick?: ((index: number) => void) | undefined
  onRowBClick?: ((index: number) => void) | undefined
  className?: string
}

/**
 * A pair of words stacked vertically (for Schrödinger crossword pairs)
 * Top word: black border, black text, white bg
 * Bottom word: inverted (black bg, white text)
 */
export function CrosswordPair({
  textA,
  textB,
  size: sizeProp,
  highlightIndex = null,
  highlightIndices = [],
  autoHighlight = false,
  isPlaced = false,
  isPlacedA,
  isPlacedB,
  onLetterClick,
  onLetterHover,
  onRowAClick,
  onRowBClick,
  className = ''
}: CrosswordPairProps) {
  const settings = useCrosswordSettings()
  const search = useSearch()

  // Use prop size if provided, otherwise use context size
  const size = sizeProp ?? settings.size

  // Get horizontal match indices for each word if autoHighlight is enabled
  const lettersA = textA.replace(/\s/g, '')
  const lettersB = textB.replace(/\s/g, '')

  const horizontalHighlightsA = autoHighlight && search
    ? search.getMatchIndices(lettersA)
    : []
  const horizontalHighlightsB = autoHighlight && search
    ? search.getMatchIndices(lettersB)
    : []

  // Get vertical match indices (same for both words)
  const verticalHighlights = autoHighlight && search
    ? search.getVerticalMatchIndices(textA, textB)
    : []

  // Base highlights from props
  const baseHighlights = [
    ...highlightIndices,
    ...(highlightIndex !== null ? [highlightIndex] : [])
  ]

  return (
    <span
      className={`relative inline-flex flex-col leading-none ${className}`}
      onMouseLeave={() => onLetterHover?.(null)}
    >
      <CrosswordWord
        text={textA}
        size={size}
        highlightIndices={[...baseHighlights, ...horizontalHighlightsA]}
        verticalHighlightIndices={verticalHighlights}
        isPlaced={isPlacedA ?? isPlaced}
        isTopWord={true}
        isBottomWord={false}
        onLetterClick={onRowAClick ?? onLetterClick}
        onLetterHover={onLetterHover}
      />
      <CrosswordWord
        text={textB}
        size={size}
        inverted
        highlightIndices={[...baseHighlights, ...horizontalHighlightsB]}
        verticalHighlightIndices={verticalHighlights}
        isPlaced={isPlacedB ?? isPlaced}
        isTopWord={false}
        isBottomWord={true}
        onLetterClick={onRowBClick ?? onLetterClick}
        onLetterHover={onLetterHover}
        className="-mt-px"
      />
    </span>
  )
}

type CrosswordTextProps = {
  children: ReactNode
  size?: Size
  muted?: boolean
  className?: string
}

/**
 * Generic wrapper that applies crossword styling to text
 * Use CrosswordWord or CrosswordPair for structured rendering
 */
export function CrosswordText({
  children,
  size = 'base',
  muted = false,
  className = ''
}: CrosswordTextProps) {
  if (typeof children !== 'string') {
    return <span className={className}>{children}</span>
  }

  return (
    <CrosswordWord text={children} size={size} muted={muted} className={className} />
  )
}
